import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { fetchUserProfile, logoutSession } from '../services/api';
import type { User } from '../contracts/types';
import { redirectToSessionExpiredLogin } from '../utils/authRedirect';

const VERIFY_DEDUPE_WINDOW_MS = 4000;
const AUTH_REDIRECT_COOLDOWN_MS = 10_000;
const AUTH_REDIRECT_TS_KEY = 'auth_redirect_last_ts';
const recentVerifyAttempts = new Map<string, number>();
const COOKIE_SESSION_MARKER = '__cookie_session__';
const MAX_COMPANY_NAME_LENGTH = 200;

const classifyAuthVerifyError = (error: unknown): 'validation' | 'auth' | 'network' | 'unknown' => {
  const status = (error as { status?: number } | null)?.status;
  const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
  if (status === 401 || status === 403) return 'auth';
  if (status === 400 || message.includes('invalid') || message.includes('required')) return 'validation';
  if (
    status === 0
    || status === 502
    || status === 503
    || status === 504
    || message.includes('network')
    || message.includes('fetch')
    || message.includes('load failed')
    || message.includes('failed to fetch')
    || message.includes('timeout')
  ) {
    return 'network';
  }
  return 'unknown';
};

interface AuthContextType {
  token: string | null;
  profile: User | null;
  signOut: () => void;
  loginWithToken: (token: string) => Promise<void>;
  authReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const authContextFallback: AuthContextType = {
  token: null,
  profile: null,
  signOut: () => undefined,
  loginWithToken: async () => undefined,
  authReady: false,
};
let warnedMissingProvider = false;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    const sessionToken = sessionStorage.getItem('auth_token');
    if (sessionToken) return sessionToken;

    // Legacy cleanup: migrate old persistent token into session-only storage.
    const legacyLocalToken = localStorage.getItem('auth_token');
    if (legacyLocalToken) {
      sessionStorage.setItem('auth_token', legacyLocalToken);
      localStorage.removeItem('auth_token');
      return legacyLocalToken;
    }

    return null;
  });
  const [profile, setProfile] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const isFetching = useRef(false);

  const logoutCleanup = () => {
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('pending_company_email');
    setToken(null);
    setProfile(null);
  };

  const hydratePendingCompanyNameFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const rawCompanyName = params.get('companyName');
    if (!rawCompanyName) return;
    const companyName = rawCompanyName.trim();
    if (!companyName || companyName.length > MAX_COMPANY_NAME_LENGTH) return;
    localStorage.setItem('pending_company_name', companyName);
  };

  const verifySession = async (
    authToken?: string | null,
    skipLoading = false,
    keepLoadingOnSuccess = false,
    allowAnonymous = false,
    refreshToken?: string | null,
  ) => {
    const tokenForVerify =
      authToken && authToken !== COOKIE_SESSION_MARKER
        ? authToken
        : null;
    const dedupeKey = `${tokenForVerify ?? COOKIE_SESSION_MARKER}|${window.location.pathname}|${window.location.search}`;
    const now = Date.now();
    const lastAttempt = recentVerifyAttempts.get(dedupeKey);
    if (lastAttempt && now - lastAttempt < VERIFY_DEDUPE_WINDOW_MS) {
      setAuthReady(true);
      return;
    }
    recentVerifyAttempts.set(dedupeKey, now);
    for (const [key, ts] of recentVerifyAttempts.entries()) {
      if (now - ts > 60_000) {
        recentVerifyAttempts.delete(key);
      }
    }

    if (isFetching.current) return;
    isFetching.current = true;
    setAuthReady(false);
    void skipLoading;
    void keepLoadingOnSuccess;

    try {
      const pendingCompanyName = localStorage.getItem('pending_company_name') || undefined;
      const userData = await fetchUserProfile(tokenForVerify, pendingCompanyName, undefined, refreshToken);
      const existingPendingCompanyName = (localStorage.getItem('pending_company_name') || '').trim();
      if (!existingPendingCompanyName && userData.companyName?.trim()) {
        localStorage.setItem('pending_company_name', userData.companyName.trim());
      }

      setProfile({
        ...userData,
        isAdmin: !!userData.isAdmin,
      });

      setToken(COOKIE_SESSION_MARKER);
      sessionStorage.setItem('auth_token', COOKIE_SESSION_MARKER);
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem(AUTH_REDIRECT_TS_KEY);

    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      const isExpectedAnonymousAuthMiss = allowAnonymous && (status === 401 || status === 403);
      const kind = classifyAuthVerifyError(err);
      const isMagicLinkContext =
        window.location.pathname === '/login'
        || window.location.pathname === '/assessment'
        || Boolean(localStorage.getItem('pending_company_email'));
      if (!isExpectedAnonymousAuthMiss) {
        console.error('verifySession error:', err);
        Sentry.addBreadcrumb({
          category: 'auth.verify',
          message: `Session verify failed (${kind})`,
          level: kind === 'validation' || kind === 'network' ? 'warning' : 'error',
          data: {
            status: status ?? null,
            flow: isMagicLinkContext ? 'magic-link' : 'auth',
          },
        });
        if (status !== 401 && status !== 403 && kind === 'unknown') {
          Sentry.captureException(err, {
            tags: {
              flow: isMagicLinkContext ? 'magic-link' : 'auth',
              error_kind: kind,
              area: 'auth-context',
            },
          });
        }
      }

      // Clear session only for explicit unauthorized responses.
      if (status === 401 || status === 403) {
        if (allowAnonymous) {
          logoutCleanup();
        } else {
          logoutCleanup();
          const now = Date.now();
          const lastRedirectTs = Number(sessionStorage.getItem(AUTH_REDIRECT_TS_KEY) || '0');
          if (now - lastRedirectTs < AUTH_REDIRECT_COOLDOWN_MS) {
            Sentry.addBreadcrumb({
              category: 'auth.verify',
              message: 'Skipped auth redirect due to cooldown guard',
              level: 'warning',
              data: {
                status,
                cooldownMs: AUTH_REDIRECT_COOLDOWN_MS,
              },
            });
            return;
          }
          sessionStorage.setItem(AUTH_REDIRECT_TS_KEY, String(now));
          redirectToSessionExpiredLogin(`${window.location.pathname}${window.location.search}`);
        }
      } else if (status === 429 || (typeof status === 'number' && status >= 500)) {
        // Preserve local session state for transient infrastructure failures.
      } else {
        // Preserve local session state for non-auth fatal transient errors.
      }
    } finally {
      isFetching.current = false;
      setAuthReady(true);
    }
  };

  const loginWithToken = async (newToken: string) => {
    setAuthReady(false);
    await verifySession(newToken, false);
  };

  const signOut = () => {
    void logoutSession().catch(() => undefined).finally(() => {
      logoutCleanup();
      window.location.href = '/';
    });
  };

  useEffect(() => {
    const handleAuth = async () => {
      hydratePendingCompanyNameFromQuery();
      const currentPath = window.location.pathname;
      const isPublicRoute =
        currentPath === '/' ||
        currentPath === '/login' ||
        currentPath === '/auth/callback' ||
        currentPath === '/auth/confirm';
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      const isResultsFromAdmin =
        window.location.pathname.startsWith('/results/') &&
        new URLSearchParams(window.location.search).get('fromAdmin') === '1';
      const keepLoadingOnSuccess = !isAdminRoute;
      const skipLoading = isResultsFromAdmin;
      let tokenFromUrl: string | null = null;
      let refreshTokenFromUrl: string | null = null;
      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        tokenFromUrl = hashParams.get('access_token');
        refreshTokenFromUrl = hashParams.get('refresh_token');
      }
      const bootstrapTokenFromSession = sessionStorage.getItem('auth_token');
      const bootstrapToken =
        tokenFromUrl
        || (bootstrapTokenFromSession && bootstrapTokenFromSession !== COOKIE_SESSION_MARKER
          ? bootstrapTokenFromSession
          : null);

      try {
        if (bootstrapToken) {
          sessionStorage.setItem('auth_token', bootstrapToken);
          localStorage.removeItem('auth_token');
          setToken(bootstrapToken);
          window.history.replaceState({}, document.title, window.location.pathname);
          await verifySession(bootstrapToken, skipLoading, keepLoadingOnSuccess, false, refreshTokenFromUrl);
        } else if (token === COOKIE_SESSION_MARKER) {
          await verifySession(undefined, skipLoading, keepLoadingOnSuccess);
        } else if (token && token !== 'null') {
          await verifySession(token, skipLoading, keepLoadingOnSuccess);
        } else if (isPublicRoute) {
          // Avoid expected 401 auth probes on public pages for anonymous users.
          setProfile(null);
          setAuthReady(true);
        } else {
          await verifySession(undefined, skipLoading, keepLoadingOnSuccess, true);
        }
      } catch (err) {
        console.error('handleAuth error:', err);
        setAuthReady(true);
      }
    };

    handleAuth();
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, profile, signOut, loginWithToken, authReady }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    if (!warnedMissingProvider) {
      warnedMissingProvider = true;
      console.warn('useAuth called outside AuthProvider; falling back to anonymous auth state.');
      Sentry.addBreadcrumb({
        category: 'auth.context',
        message: 'useAuth called outside AuthProvider (fallback state applied)',
        level: 'warning',
      });
    }
    return authContextFallback;
  }
  return context;
};

